import React, { useState } from "react";
import "../../css/Login.css";
import logo from "../../components/images/logo-transparent-png.png";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Footer from "../../components/footer/Footer";
import Header from "../../components/header/Header";
import { toast } from "react-hot-toast";
import { useAuth } from "../../context/auth";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import LoginIcon from "@mui/icons-material/Login";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [auth, setAuth] = useAuth();

  const apiUrl = `${process.env.REACT_APP_API}`;

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEmailChange = (event) => {
    const value = event.target.value;
    setEmail(value);
    setTouchedFields({ ...touchedFields, email: true });

    // Real-time validation
    if (touchedFields.email) {
      const newErrors = { ...errors };
      if (!value.trim()) {
        newErrors.email = "Email is required";
      } else if (!validateEmail(value)) {
        newErrors.email = "Please enter a valid email address";
      } else {
        delete newErrors.email;
      }
      setErrors(newErrors);
    }
  };

  const handlePasswordChange = (event) => {
    const value = event.target.value;
    setPassword(value);
    setTouchedFields({ ...touchedFields, password: true });

    // Real-time validation
    if (touchedFields.password) {
      const newErrors = { ...errors };
      if (!value) {
        newErrors.password = "Password is required";
      } else if (value.length < 6) {
        newErrors.password = "Password must be at least 6 characters";
      } else {
        delete newErrors.password;
      }
      setErrors(newErrors);
    }
  };

  const handleBlur = (field) => {
    setTouchedFields({ ...touchedFields, [field]: true });
    validateForm();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Mark all fields as touched
    setTouchedFields({ email: true, password: true });

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${apiUrl}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        // Save token and currentUser to local storage
        localStorage.setItem("auth", JSON.stringify(data));
        setAuth({
          ...auth,
          token: data.token,
          currentUser: {
            id: data.currentUser.id,
            name: {
              first: data.currentUser.name.first,
              last: data.currentUser.name.last,
            },
            email: data.currentUser.email,
            address: {
              street1: data.currentUser.address.street1,
              street2: data.currentUser.address.street2,
              city: data.currentUser.address.city,
              state: data.currentUser.address.state,
              zip: data.currentUser.address.zip,
            },
            phone: data.currentUser.phone,
          },
        });
        toast.success("Welcome back! Login successful");

        // Redirect
        navigate(
            location.state && location.state.from ? location.state.from : "/",
            { replace: true }
        );
      } else {
        // Handle login error
        setIsSubmitting(false);
        if (response.status === 401) {
          toast.error("Invalid email or password");
          setErrors({
            submit: "The email or password you entered is incorrect. Please try again."
          });
        } else {
          toast.error("Login failed. Please try again.");
          setErrors({ submit: data.message || "An error occurred during login" });
        }
      }
    } catch (error) {
      console.error("Error:", error);
      setIsSubmitting(false);
      toast.error("Connection error. Please check your internet and try again.");
      setErrors({ submit: "Unable to connect to the server. Please try again later." });
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
      <>
        <Header />
        <div className="login-page">
          <div className="container py-5">
            <div className="row justify-content-center">
              <div className="col-lg-5 col-md-7 col-sm-9">
                <div className="login-card">
                  {/* Logo */}
                  <div className="text-center mb-4">
                    <img
                        src={logo}
                        alt="Logo"
                        className="login-logo"
                    />
                  </div>

                  {/* Welcome Text */}
                  <div className="text-center mb-4">
                    <h2 className="login-title">Welcome Back!</h2>
                    <p className="login-subtitle">Sign in to continue shopping</p>
                  </div>

                  {/* Login Form */}
                  <form onSubmit={handleSubmit}>
                    {/* Submit Error Alert */}
                    {errors.submit && (
                        <div className="alert alert-danger d-flex align-items-center" role="alert">
                          <ErrorOutlineIcon className="me-2" />
                          <div>{errors.submit}</div>
                        </div>
                    )}

                    {/* Email Field */}
                    <div className="form-group mb-4">
                      <label htmlFor="email" className="form-label">
                        <EmailIcon fontSize="small" className="me-1" />
                        Email Address
                      </label>
                      <input
                          type="email"
                          className={`form-control form-control-lg ${
                              touchedFields.email && errors.email ? "is-invalid" : ""
                          } ${touchedFields.email && !errors.email && email ? "is-valid" : ""}`}
                          id="email"
                          placeholder="Enter your email"
                          value={email}
                          onChange={handleEmailChange}
                          onBlur={() => handleBlur("email")}
                          disabled={isSubmitting}
                      />
                      {touchedFields.email && errors.email && (
                          <div className="invalid-feedback d-flex align-items-center">
                            <ErrorOutlineIcon fontSize="small" className="me-1" />
                            {errors.email}
                          </div>
                      )}
                      {touchedFields.email && !errors.email && email && (
                          <div className="valid-feedback">
                            Looks good!
                          </div>
                      )}
                    </div>

                    {/* Password Field */}
                    <div className="form-group mb-4">
                      <label htmlFor="password" className="form-label">
                        <LockIcon fontSize="small" className="me-1" />
                        Password
                      </label>
                      <div className="password-input-wrapper">
                        <input
                            type={showPassword ? "text" : "password"}
                            className={`form-control form-control-lg ${
                                touchedFields.password && errors.password ? "is-invalid" : ""
                            } ${touchedFields.password && !errors.password && password ? "is-valid" : ""}`}
                            id="password"
                            placeholder="Enter your password"
                            value={password}
                            onChange={handlePasswordChange}
                            onBlur={() => handleBlur("password")}
                            disabled={isSubmitting}
                        />
                        <button
                            type="button"
                            className="password-toggle-btn"
                            onClick={togglePasswordVisibility}
                            tabIndex="-1"
                        >
                          {showPassword ? (
                              <VisibilityOffIcon fontSize="small" />
                          ) : (
                              <VisibilityIcon fontSize="small" />
                          )}
                        </button>
                      </div>
                      {touchedFields.password && errors.password && (
                          <div className="invalid-feedback d-flex align-items-center" style={{ display: "block !important" }}>
                            <ErrorOutlineIcon fontSize="small" className="me-1" />
                            {errors.password}
                          </div>
                      )}
                    </div>

                    {/* Forgot Password Link */}
                    <div className="text-end mb-4">
                      <Link to="/forgotpassword" className="forgot-password-link">
                        Forgot Password?
                      </Link>
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        className="btn login-button w-100 mb-3"
                        disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Signing In...
                          </>
                      ) : (
                          <>
                            <LoginIcon className="me-2" />
                            Sign In
                          </>
                      )}
                    </button>

                    {/* Divider */}
                    <div className="divider my-4">
                      <span>OR</span>
                    </div>

                    {/* Sign Up Link */}
                    <div className="text-center">
                      <p className="mb-2 text-muted">Don't have an account?</p>
                      <Link to="/signup" className="btn signup-button w-100">
                        <PersonAddIcon className="me-2" />
                        Create Account
                      </Link>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
  );
};

export default LoginPage;